/**
 * atelier-muse.js — THE MUSE'S CANVAS. An unfinished mythological
 * mural that the visitor completes with their own brush.
 *
 * How it works:
 *  - A finished mural (bone temple with noir ink lines, two dark
 *    cypresses, ONE low cherry sun burning through rose haze, the Muse
 *    in a maroon chiton at the right) is painted once into an offscreen
 *    canvas. The value ladder is the new house law: cotton/parchment
 *    dominant, noir as ink, maroon as shadow mass, one cherry event.
 *  - The visible canvas starts as raw cotton canvas — bone weave, gesso
 *    patches, a noir charcoal sketch, a few abandoned first washes.
 *    Dragging the brush reveals the finished painting through
 *    ragged-edged daubs (a mask canvas composited with destination-in),
 *    so the visitor literally paints it back.
 *  - Four pigment wells (cotton, cherry, maroon, noir) change the
 *    brush's character: each reveals with a different width and grain.
 *  - Progress milestones surface short captions drawn from the section
 *    settings (real portfolio fragments, never invented), and at
 *    completion the mural settles and offers the studio door.
 *  - Audio (always optional-chained): scene 'muse-game' while mounted,
 *    throttled 'brush' cues while revealing, 'pluck' at milestones,
 *    'bell' at completion.
 *
 * Touch: one-finger painting, page scroll preserved outside the mural.
 * Reduced motion: painting still works (it is user-initiated), but all
 * ambient drift stops. A full text alternative sits below the canvas.
 * Debug (?atlDebug=1): window.AtelierMuseDebug.revealAll().
 */
(() => {
  const boot = (A) => {
    A.register('muse', (root) => {
      const stage = root.querySelector('[data-muse-stage]');
      const canvas = root.querySelector('[data-muse-canvas]');
      const progressEl = root.querySelector('[data-muse-progress]');
      const captionEl = root.querySelector('[data-muse-caption]');
      const doneEl = root.querySelector('[data-muse-done]');
      const resetBtn = root.querySelector('[data-muse-reset]');
      const wells = Array.from(root.querySelectorAll('[data-muse-well]'));
      if (!canvas || !stage) return () => {};
      const ctx = canvas.getContext('2d');
      if (!ctx) return () => {};

      const { clamp, lerp, hash } = A.helpers;
      const mobile = window.matchMedia('(max-width: 749px)').matches;
      const small = window.matchMedia('(max-width: 768px)');

      /* Milestone captions from the section (real fragments). */
      let CAPTIONS = [];
      try {
        const el = root.querySelector('[data-muse-data]');
        if (el) CAPTIONS = JSON.parse(el.textContent).captions || [];
      } catch (e) {
        CAPTIONS = [];
      }

      /* Four pigments + sanctioned mixes only. */
      const P = {
        noir: '#1b1717',
        noirDeep: '#120e0e',
        maroon: '#630000',
        maroonDark: '#3d0806',
        umber: '#3a2422',
        cherry: '#810100',
        cotton: '#edebdd',
        parchment: '#f4f2e8',
        bone: '#ddd8c6',
        cottonShadow: '#d8d2bd',
        rose: '#d9a9a2',
        blush: '#e4c9bf',
      };
      const tint = (hex, a) => {
        const n = parseInt(hex.slice(1), 16);
        return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
      };

      let W = 0;
      let H = 0;
      let dpr = 1;
      let finished = null; // the completed mural
      let under = null; // the raw-canvas underpainting
      let mask = null; // revealed areas
      let mctx = null;
      let revealed = 0;
      let milestone = 0;
      let complete = false;
      let comp = null;
      let compCtx = null;

      const rng = (seed) => {
        let n = seed * 977 + 13;
        return () => {
          n += 1;
          return hash(n);
        };
      };

      /* One composition, shared by the finished mural AND the charcoal
         sketch beneath — so the sketch always matches what appears. */
      const layout = (w, h) => ({
        horizon: h * 0.72,
        base: h * 0.74,
        sun: { x: w * 0.68, y: h * 0.4, r: h * 0.048 },
        temple: { x: w * 0.3, y: h * 0.74, w: Math.min(w * 0.3, 280), h: Math.min(h * 0.42, 210) },
        cyp: [
          { x: w * 0.52, h: h * 0.34 },
          { x: w * 0.56, h: h * 0.28 },
        ],
        muse: { x: w * 0.8, y: h * 0.74 },
      });

      const taper = (g, x0, y0, x1, y1, w, color, alpha, segs) => {
        const n = segs || 6;
        g.strokeStyle = color;
        g.globalAlpha = alpha;
        g.lineCap = 'round';
        for (let i = 0; i < n; i++) {
          const t0 = i / n;
          const t1 = (i + 1) / n;
          g.lineWidth = Math.max(w * Math.sin(((t0 + t1) / 2) * Math.PI), 0.6);
          g.beginPath();
          g.moveTo(lerp(x0, x1, t0), lerp(y0, y1, t0));
          g.lineTo(lerp(x0, x1, t1), lerp(y0, y1, t1));
          g.stroke();
        }
        g.globalAlpha = 1;
      };

      /* A hand-inked line — jittered points, never ruler-straight. */
      const ink = (g, pts, r, alpha, close, width) => {
        g.strokeStyle = P.noir;
        g.globalAlpha = alpha;
        g.lineWidth = width || 1.3;
        g.lineJoin = 'round';
        g.lineCap = 'round';
        g.beginPath();
        pts.forEach((p, i) => {
          const jx = p[0] + (r() - 0.5) * 2.4;
          const jy = p[1] + (r() - 0.5) * 2.4;
          if (i === 0) g.moveTo(jx, jy);
          else g.lineTo(jx, jy);
        });
        if (close) g.closePath();
        g.stroke();
        g.globalAlpha = 1;
      };

      /* ---- Paint the finished mural once — cotton-dominant ---- */
      const paintFinished = (g, w, h) => {
        const r = rng(7);
        const L = layout(w, h);

        /* Sky: a parchment wash rebuilt in long visible strokes. */
        g.fillStyle = P.parchment;
        g.fillRect(0, 0, w, h);
        for (let i = 0; i < 54; i++) {
          const y = r() * L.horizon;
          const pick = r();
          const col = pick < 0.34 ? P.cotton : pick < 0.6 ? P.parchment : pick < 0.84 ? P.bone : P.cottonShadow;
          taper(g, r() * w - 60, y, r() * w + 140, y + (r() - 0.5) * 10, 6 + r() * 16, col, 0.1 + r() * 0.1, 4);
        }
        /* Rose haze breathing around the sun's height. */
        for (let i = 0; i < 12; i++) {
          const y = L.sun.y + (r() - 0.4) * h * 0.16;
          taper(g, r() * w - 40, y, r() * w + 120, y + (r() - 0.5) * 6, 4 + r() * 10, P.rose, 0.06 + r() * 0.07, 4);
        }
        /* Pale maroon washes settling toward the horizon. */
        for (let i = 0; i < 6; i++) {
          const y = L.horizon - r() * h * 0.07;
          taper(g, r() * w - 60, y, r() * w + 160, y + (r() - 0.5) * 5, 8 + r() * 12, P.maroon, 0.035 + r() * 0.03, 4);
        }

        /* A distant pale-maroon ridge, barely there. */
        g.globalAlpha = 0.1;
        g.fillStyle = P.maroon;
        g.beginPath();
        g.moveTo(-10, L.horizon);
        for (let x = 0; x <= w; x += 26) {
          g.lineTo(x, L.horizon - h * (0.025 + 0.035 * Math.abs(Math.sin(x * 0.004 + 1.7))));
        }
        g.lineTo(w + 10, L.horizon);
        g.closePath();
        g.fill();
        g.globalAlpha = 1;

        /* THE one cherry event — a low sun burning through haze. */
        const sx = L.sun.x;
        const sy = L.sun.y;
        const sr = L.sun.r;
        const glow = g.createRadialGradient(sx, sy, sr * 0.4, sx, sy, sr * 5.2);
        glow.addColorStop(0, tint(P.rose, 0.5));
        glow.addColorStop(0.55, tint(P.rose, 0.16));
        glow.addColorStop(1, tint(P.rose, 0));
        g.fillStyle = glow;
        g.fillRect(sx - sr * 6, sy - sr * 6, sr * 12, sr * 12);
        g.fillStyle = tint(P.cherry, 0.92);
        g.beginPath();
        g.arc(sx, sy, sr, 0, Math.PI * 2);
        g.fill();
        /* Ragged pigment edge, then haze strokes crossing the disc. */
        for (let i = 0; i < 7; i++) {
          const a = r() * Math.PI * 2;
          taper(
            g,
            sx + Math.cos(a) * sr * 0.8,
            sy + Math.sin(a) * sr * 0.8,
            sx + Math.cos(a) * sr * 1.14,
            sy + Math.sin(a) * sr * 1.14,
            2.5 + r() * 3,
            P.cherry,
            0.5,
            3
          );
        }
        taper(g, sx - sr * 2.2, sy - sr * 0.25, sx + sr * 2.4, sy - sr * 0.2, sr * 0.34, P.blush, 0.75, 5);
        taper(g, sx - sr * 1.8, sy + sr * 0.35, sx + sr * 2.0, sy + sr * 0.42, sr * 0.26, P.parchment, 0.6, 5);

        /* Ground: warm bone field, maroon washes, noir ink grasses. */
        g.fillStyle = P.bone;
        g.beginPath();
        g.moveTo(0, h);
        for (let x = 0; x <= w; x += 24) g.lineTo(x, L.horizon + Math.sin(x * 0.008) * h * 0.03);
        g.lineTo(w, h);
        g.closePath();
        g.fill();
        for (let i = 0; i < 26; i++) {
          const x = r() * w;
          const y = h * (0.76 + r() * 0.22);
          const pick = r();
          const col = pick < 0.45 ? P.cottonShadow : pick < 0.8 ? P.maroon : P.umber;
          const al = col === P.cottonShadow ? 0.18 + r() * 0.14 : 0.08 + r() * 0.1;
          taper(g, x, y, x + 40 + r() * 60, y + (r() - 0.5) * 8, 5 + r() * 10, col, al, 4);
        }
        for (let i = 0; i < 34; i++) {
          const x = r() * w;
          const y = h * (0.78 + r() * 0.19);
          taper(g, x, y, x + 4 + r() * 9, y - 4 - r() * 8, 1.6, P.noir, 0.22 + r() * 0.2, 2);
        }

        /* Temple on the left third — lit parchment, bone shade,
           hand-inked noir edges, a maroon breath under the cornice. */
        const T = L.temple;
        const tx = T.x;
        const ty = T.y;
        const tw = T.w;
        const th = T.h;
        g.fillStyle = tint(P.umber, 0.14);
        g.beginPath();
        g.ellipse(tx, ty + th * 0.02, tw * 0.62, th * 0.045, 0, 0, Math.PI * 2);
        g.fill();
        g.fillStyle = P.parchment;
        g.fillRect(tx - tw / 2 - 8, ty - th * 0.08, tw + 16, th * 0.08);
        const cols = 6;
        const gap = tw / (cols - 1);
        for (let i = 0; i < cols; i++) {
          const cx = tx - tw / 2 + i * gap;
          const cw = tw * 0.035;
          g.fillStyle = P.cotton;
          g.fillRect(cx - cw / 2, ty - th * 0.7, cw, th * 0.62);
          /* shade on the off-sun (left) flank of each drum */
          g.fillStyle = tint(P.bone, 0.9);
          g.fillRect(cx - cw / 2, ty - th * 0.7, cw * 0.4, th * 0.62);
          g.fillStyle = P.parchment;
          g.fillRect(cx - cw * 0.8, ty - th * 0.72, cw * 1.6, th * 0.03);
        }
        g.fillStyle = P.cotton;
        g.fillRect(tx - tw / 2 - 6, ty - th * 0.78, tw + 12, th * 0.05);
        g.fillStyle = P.parchment;
        g.beginPath();
        g.moveTo(tx - tw / 2 - 10, ty - th * 0.78);
        g.lineTo(tx, ty - th * 0.78 - tw * 0.13);
        g.lineTo(tx + tw / 2 + 10, ty - th * 0.78);
        g.closePath();
        g.fill();
        g.fillStyle = tint(P.maroon, 0.12);
        g.fillRect(tx - tw / 2 - 6, ty - th * 0.73, tw + 12, th * 0.014);
        ink(
          g,
          [
            [tx - tw / 2 - 10, ty - th * 0.78],
            [tx, ty - th * 0.78 - tw * 0.13],
            [tx + tw / 2 + 10, ty - th * 0.78],
          ],
          r,
          0.5,
          true
        );
        g.strokeStyle = tint(P.noir, 0.3);
        g.lineWidth = 1.1;
        for (let i = 0; i < cols; i++) {
          const cx = tx - tw / 2 + i * gap;
          const cw = tw * 0.035;
          g.beginPath();
          g.moveTo(cx - cw / 2 + (r() - 0.5), ty - th * 0.7);
          g.lineTo(cx - cw / 2 + (r() - 0.5), ty - th * 0.08);
          g.moveTo(cx + cw / 2 + (r() - 0.5), ty - th * 0.7);
          g.lineTo(cx + cw / 2 + (r() - 0.5), ty - th * 0.08);
          g.stroke();
        }
        ink(
          g,
          [
            [tx - tw / 2 - 8, ty - th * 0.08],
            [tx + tw / 2 + 8, ty - th * 0.08],
          ],
          r,
          0.4
        );

        /* Cypress pair right of centre — the mural's noir ink mass. */
        L.cyp.forEach((c, i) => {
          const rr = rng(31 + i);
          g.fillStyle = tint(P.umber, 0.18);
          g.beginPath();
          g.ellipse(c.x, L.base + 3, 16, 4, 0, 0, Math.PI * 2);
          g.fill();
          for (let s = 0; s < 14; s++) {
            const t = s / 14;
            taper(
              g,
              c.x + (rr() - 0.5) * 5,
              L.base - t * c.h,
              c.x + (rr() - 0.5) * 5,
              L.base - Math.min(t + 0.15, 1) * c.h,
              c.h * 0.14 * Math.sin(Math.min(t * 1.3, 1) * Math.PI) + 2,
              rr() > 0.4 ? P.noirDeep : P.maroonDark,
              0.85,
              3
            );
          }
        });

        /* The Muse — maroon chiton with cotton trim on the light
           ground (the visibility law), rose skin, noir hair, a
           parchment rim-light on her sun side, and her lyre. */
        const mx = L.muse.x;
        const my = L.muse.y;
        g.fillStyle = tint(P.umber, 0.22);
        g.beginPath();
        g.ellipse(mx + 2, my - 2, 26, 6, 0, 0, Math.PI * 2);
        g.fill();
        taper(g, mx, my - 64, mx, my - 6, 30, P.maroon, 0.96, 6);
        taper(g, mx + 6, my - 58, mx + 8, my - 10, 12, P.maroonDark, 0.55, 4);
        taper(g, mx - 8, my - 56, mx - 9, my - 12, 5, P.cotton, 0.75, 4);
        taper(g, mx - 2, my - 30, mx + 1, my - 8, 9, P.cotton, 0.35, 4);
        g.fillStyle = P.rose;
        g.beginPath();
        g.arc(mx, my - 74, 9, 0, Math.PI * 2);
        g.fill();
        taper(g, mx - 8, my - 80, mx + 9, my - 76, 8, P.noir, 0.9, 4);
        taper(g, mx + 6, my - 74, mx + 10, my - 62, 5, P.noir, 0.8, 3);
        taper(g, mx - 14, my - 70, mx - 15, my - 14, 2.6, P.parchment, 0.8, 5);
        g.strokeStyle = P.umber;
        g.lineWidth = 2.4;
        g.beginPath();
        g.arc(mx - 16, my - 46, 9, Math.PI * 0.9, Math.PI * 2.1);
        g.stroke();
        g.strokeStyle = P.noir;
        g.lineWidth = 1;
        for (let i = 0; i < 4; i++) {
          g.beginPath();
          g.moveTo(mx - 22 + i * 4, my - 52);
          g.lineTo(mx - 21 + i * 4, my - 38);
          g.stroke();
        }

        /* Dry-brush scumble pass — canvas tooth over everything. */
        for (let i = 0; i < 24; i++) {
          const x0 = r() * w;
          const y0 = r() * h;
          taper(g, x0, y0, x0 + 60 + r() * 120, y0 + (r() - 0.5) * 30, 10 + r() * 20, r() > 0.5 ? P.parchment : P.cotton, 0.04, 3);
        }
      };

      /* ---- The raw canvas — bright, unfinished, waiting ---- */
      const paintUnder = (g, w, h) => {
        const r = rng(3);
        const L = layout(w, h);
        /* Bone linen with visible weave — raw canvas, not murk. */
        g.fillStyle = P.bone;
        g.fillRect(0, 0, w, h);
        for (let i = 0; i < 44; i++) {
          const y = r() * h;
          taper(g, r() * w - 60, y, r() * w + 110, y + (r() - 0.5) * 14, 6 + r() * 16, r() > 0.5 ? P.cottonShadow : P.cotton, 0.16 + r() * 0.14, 4);
        }
        g.strokeStyle = tint(P.umber, 0.05);
        g.lineWidth = 1;
        for (let y = 0; y < h; y += 7 + Math.floor(r() * 4)) {
          g.beginPath();
          g.moveTo(0, y + (r() - 0.5) * 2);
          g.lineTo(w, y + (r() - 0.5) * 2);
          g.stroke();
        }
        /* Gesso patches where the painter primed. */
        for (let i = 0; i < 10; i++) {
          const x = r() * w;
          const y = r() * h;
          taper(g, x, y, x + 50 + r() * 90, y + (r() - 0.5) * 24, 16 + r() * 26, P.parchment, 0.12, 3);
        }
        /* First thin washes, abandoned mid-thought. */
        taper(g, w * 0.06, h * 0.86, w * 0.42, h * 0.9, 26, P.maroon, 0.07, 5);
        taper(g, L.sun.x - 60, L.sun.y + 8, L.sun.x + 80, L.sun.y + 2, 20, P.rose, 0.12, 5);
        taper(g, w * 0.6, h * 0.8, w * 0.95, h * 0.84, 22, P.umber, 0.06, 5);

        /* Charcoal sketch of what waits beneath — hand-wobbled noir. */
        g.strokeStyle = tint(P.noir, 0.38);
        g.lineWidth = 1.4;
        g.lineJoin = 'round';
        g.lineCap = 'round';
        const line = (pts, close) => {
          g.beginPath();
          pts.forEach((p, i) => {
            const jx = p[0] + (r() - 0.5) * 3;
            const jy = p[1] + (r() - 0.5) * 3;
            if (i === 0) g.moveTo(jx, jy);
            else g.lineTo(jx, jy);
          });
          if (close) g.closePath();
          g.stroke();
        };
        for (let x = 0; x < w; x += 60) line([[x + 6, L.horizon], [x + 40, L.horizon + (r() - 0.5) * 4]]);
        const T = L.temple;
        line([
          [T.x - T.w / 2, T.y],
          [T.x - T.w / 2, T.y - T.h * 0.78],
          [T.x + T.w / 2, T.y - T.h * 0.78],
          [T.x + T.w / 2, T.y],
        ]);
        line([
          [T.x - T.w / 2 - 10, T.y - T.h * 0.78],
          [T.x, T.y - T.h * 0.78 - T.w * 0.13],
          [T.x + T.w / 2 + 10, T.y - T.h * 0.78],
        ]);
        for (let i = 1; i < 5; i++) {
          const cx = T.x - T.w / 2 + (T.w / 5) * i;
          line([
            [cx, T.y - T.h * 0.7],
            [cx, T.y - T.h * 0.1],
          ]);
        }
        L.cyp.forEach((c) =>
          line([
            [c.x, L.base + h * 0.01],
            [c.x + (r() - 0.5) * 6, L.base - c.h],
          ])
        );
        g.beginPath();
        g.arc(L.sun.x, L.sun.y, L.sun.r, 0, Math.PI * 2);
        g.stroke();
        g.beginPath();
        g.arc(L.muse.x, L.muse.y - 74, 9, 0, Math.PI * 2);
        g.stroke();
        line([
          [L.muse.x - 10, L.muse.y - 62],
          [L.muse.x, L.muse.y - 40],
          [L.muse.x - 6, L.muse.y - 8],
        ]);
        line([
          [L.muse.x + 10, L.muse.y - 60],
          [L.muse.x + 4, L.muse.y - 8],
        ]);
        g.beginPath();
        g.arc(L.muse.x - 16, L.muse.y - 46, 9, Math.PI * 0.9, Math.PI * 2.1);
        g.stroke();
      };

      const build = (c, painter) => {
        c.width = Math.round(W * dpr);
        c.height = Math.round(H * dpr);
        const g = c.getContext('2d');
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        painter(g, W, H);
        return g;
      };

      const fit = () => {
        const rect = stage.getBoundingClientRect();
        /* DPR caps per the house law: ≤1.5 desktop, ≤1.25 small. */
        dpr = Math.min(window.devicePixelRatio || 1, small.matches ? 1.25 : 1.5);
        W = Math.max(1, Math.round(rect.width));
        H = Math.max(1, Math.round(rect.height));
        canvas.width = Math.round(W * dpr);
        canvas.height = Math.round(H * dpr);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        finished = document.createElement('canvas');
        build(finished, paintFinished);
        under = document.createElement('canvas');
        build(under, paintUnder);
        const keep = mask; // preserve progress across resize when possible
        mask = document.createElement('canvas');
        mask.width = Math.round(W * dpr);
        mask.height = Math.round(H * dpr);
        mctx = mask.getContext('2d');
        if (keep) mctx.drawImage(keep, 0, 0, mask.width, mask.height);
        comp = document.createElement('canvas');
        comp.width = mask.width;
        comp.height = mask.height;
        compCtx = comp.getContext('2d');
        compose();
      };

      /* ---- Brush wells ---- */
      let pigment = 'cotton';
      const WELL_STYLE = {
        cotton: { r: 46, soft: 0.5 },
        cherry: { r: 30, soft: 0.75 },
        maroon: { r: 62, soft: 0.4 },
        noir: { r: 22, soft: 0.9 },
      };
      wells.forEach((wl) => {
        wl.addEventListener('click', () => {
          pigment = wl.dataset.museWell;
          wells.forEach((o) => o.setAttribute('aria-pressed', o === wl ? 'true' : 'false'));
        });
      });

      /* ---- Reveal painting ---- */
      const reveal = (x, y, dx, dy) => {
        if (complete || !mctx) return;
        const st = WELL_STYLE[pigment] || WELL_STYLE.cotton;
        const speed = Math.min(Math.hypot(dx, dy), 60);
        const r = (st.r + speed * 0.4) * (mobile ? 0.8 : 1);
        const ang = Math.atan2(dy, dx);
        mctx.save();
        mctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        mctx.translate(x, y);
        mctx.rotate(ang + (Math.random() - 0.5) * 0.4);
        mctx.scale(1, 0.45 + Math.random() * 0.25);
        const grad = mctx.createRadialGradient(0, 0, 0, 0, 0, r);
        grad.addColorStop(0, 'rgba(0,0,0,1)');
        grad.addColorStop(st.soft, 'rgba(0,0,0,0.85)');
        grad.addColorStop(1, 'rgba(0,0,0,0)');
        mctx.fillStyle = grad;
        mctx.beginPath();
        mctx.arc(0, 0, r, 0, Math.PI * 2);
        mctx.fill();
        mctx.restore();
      };

      const compose = () => {
        if (!compCtx) return;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(under, 0, 0);
        /* finished mural through the revealed mask — one reused buffer */
        compCtx.setTransform(1, 0, 0, 1, 0, 0);
        compCtx.globalCompositeOperation = 'source-over';
        compCtx.clearRect(0, 0, comp.width, comp.height);
        compCtx.drawImage(finished, 0, 0);
        compCtx.globalCompositeOperation = 'destination-in';
        compCtx.drawImage(mask, 0, 0);
        ctx.drawImage(comp, 0, 0);
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };

      /* Progress: coarse sample of the mask (never per-frame). */
      const measure = () => {
        if (!mctx) return;
        const gw = 24;
        const gh = 14;
        const small2 = document.createElement('canvas');
        small2.width = gw;
        small2.height = gh;
        const sg = small2.getContext('2d');
        sg.drawImage(mask, 0, 0, gw, gh);
        const data = sg.getImageData(0, 0, gw, gh).data;
        let sum = 0;
        for (let i = 3; i < data.length; i += 4) sum += data[i];
        revealed = sum / (255 * gw * gh);
        if (progressEl) progressEl.style.setProperty('--muse-progress', String(clamp(revealed / 0.82, 0, 1)));

        const stages = [0.16, 0.34, 0.55, 0.82];
        while (milestone < stages.length && revealed >= stages[milestone]) {
          const cap = CAPTIONS[milestone];
          if (cap && captionEl) {
            captionEl.textContent = cap;
            captionEl.classList.remove('is-fresh');
            void captionEl.offsetWidth;
            captionEl.classList.add('is-fresh');
            A.helpers.announce(cap);
          }
          window.AtelierAudio?.event('pluck');
          milestone += 1;
        }
        if (revealed >= 0.82 && !complete) {
          complete = true;
          window.AtelierAudio?.event('bell');
          /* settle: reveal everything */
          mctx.save();
          mctx.setTransform(1, 0, 0, 1, 0, 0);
          mctx.fillStyle = 'rgba(0,0,0,1)';
          mctx.fillRect(0, 0, mask.width, mask.height);
          mctx.restore();
          compose();
          if (doneEl) {
            doneEl.hidden = false;
            const focusable = doneEl.querySelector('a, button');
            if (focusable) focusable.focus();
          }
          A.helpers.announce(root.dataset.museDoneMessage || 'The mural is complete.');
        }
      };

      /* ---- Pointer painting ---- */
      let painting = false;
      let px = 0;
      let py = 0;
      let strokeDirty = false;
      let lastBrushCue = 0;

      const brushCue = () => {
        const now = performance.now();
        if (now - lastBrushCue > 170) {
          lastBrushCue = now;
          window.AtelierAudio?.event('brush');
        }
      };

      const toLocal = (e) => {
        const rect = canvas.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
      };

      const down = (e) => {
        if (complete) return;
        painting = true;
        const p = toLocal(e);
        px = p.x;
        py = p.y;
        reveal(p.x, p.y, 1, 0);
        strokeDirty = true;
        compose();
        brushCue();
        canvas.setPointerCapture(e.pointerId);
      };
      const move = (e) => {
        if (!painting) return;
        const p = toLocal(e);
        const dx = p.x - px;
        const dy = p.y - py;
        const dist = Math.hypot(dx, dy);
        const steps = Math.max(1, Math.min(Math.floor(dist / 10), 8));
        for (let i = 1; i <= steps; i++) {
          reveal(px + (dx * i) / steps, py + (dy * i) / steps, dx, dy);
        }
        px = p.x;
        py = p.y;
        strokeDirty = true;
        compose();
        brushCue();
      };
      const up = () => {
        if (!painting) return;
        painting = false;
        if (strokeDirty) {
          strokeDirty = false;
          measure();
        }
      };
      canvas.addEventListener('pointerdown', down);
      canvas.addEventListener('pointermove', move);
      canvas.addEventListener('pointerup', up);
      canvas.addEventListener('pointercancel', up);

      const doReset = () => {
        complete = false;
        milestone = 0;
        revealed = 0;
        if (mctx) {
          mctx.save();
          mctx.setTransform(1, 0, 0, 1, 0, 0);
          mctx.clearRect(0, 0, mask.width, mask.height);
          mctx.restore();
        }
        if (doneEl) doneEl.hidden = true;
        if (captionEl) captionEl.textContent = root.dataset.museOpeningCaption || '';
        if (progressEl) progressEl.style.setProperty('--muse-progress', '0');
        compose();
      };
      if (resetBtn) resetBtn.addEventListener('click', doReset);

      const onResize = A.helpers.onFrame(() => {
        fit();
      });
      window.addEventListener('resize', onResize, { passive: true });

      fit();
      if (captionEl && root.dataset.museOpeningCaption) captionEl.textContent = root.dataset.museOpeningCaption;
      window.AtelierAudio?.setScene('muse-game');

      /* Debug hook for the QA harness — only under ?atlDebug=1. */
      let debugAttached = false;
      if (location.search.indexOf('atlDebug=1') !== -1) {
        window.AtelierMuseDebug = {
          revealAll() {
            if (!mctx) return;
            mctx.save();
            mctx.setTransform(1, 0, 0, 1, 0, 0);
            mctx.fillStyle = 'rgba(0,0,0,1)';
            mctx.fillRect(0, 0, mask.width, mask.height);
            mctx.restore();
            compose();
            measure();
          },
        };
        debugAttached = true;
      }

      return () => {
        window.AtelierAudio?.setScene('none');
        if (debugAttached) delete window.AtelierMuseDebug;
        window.removeEventListener('resize', onResize);
        canvas.removeEventListener('pointerdown', down);
        canvas.removeEventListener('pointermove', move);
        canvas.removeEventListener('pointerup', up);
        canvas.removeEventListener('pointercancel', up);
      };
    });
  };

  if (window.Atelier && window.Atelier.__ready) boot(window.Atelier);
  else document.addEventListener('atelier:ready', (e) => boot(e.detail), { once: true });
})();
